import { CSSProperties, ReactNode, useCallback, useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import styled from '@emotion/styled'

import { Flex } from './Flex'
import InfoPill from './InfoPill'
import { StyledIcon } from './StyledIcon'
import { Label, SmallLabel } from './Typography'

import { CheckCircle, Copy } from '@/assets/icons'
import { colors } from '@/constants/colors'
import truncate from '@/utils/truncate'

const DefaultKeyBox = styled(Flex)`
  position: relative;
  width: 100%;
  align-items: start;
  gap: 0.7rem;
`

const KeyBox = styled.div`
  width: 100%;
  padding: 1.25rem;
  background-color: ${colors.darkBlue3};
  border-radius: 1.25rem;

  ${DefaultKeyBox} {
    justify-content: space-between;
    gap: 2.25rem;
  }
`

const StyledCopy = styled(StyledIcon)`
  min-width: 18px;
`

interface CopyProps {
  content: ReactNode
  copyContent?: string
  frontChars?: number
  backChars?: number
  wrapperStyle?: CSSProperties
  onCopy?: () => void
  showCopiedIndicator?: boolean
}

export function CopyLabel({
  content,
  copyContent,
  frontChars = 16,
  backChars = 10,
  wrapperStyle,
  onCopy: handleCopy,
  showCopiedIndicator = true,
}: CopyProps) {
  const innerCopyContent =
    typeof content === 'string'
      ? (copyContent ?? (content as string))
      : (copyContent ?? '')
  const [copied, setCopied] = useState(false)
  // ReturnType<typeof setTimeout>, not NodeJS.Timeout: this runs in the browser,
  // where setTimeout returns a number. The NodeJS namespace only resolved here by
  // accident, via @types/node pulled in transitively by the vercel CLI.
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const buttonRef = useRef<HTMLDivElement>(null)
  const [pillPosition, setPillPosition] = useState<{
    top: number
    left: number
  } | null>(null)

  const onCopy = useCallback(
    (text: string) => {
      navigator.clipboard.writeText(text)

      if (showCopiedIndicator && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect()
        setPillPosition({
          top: rect.top,
          left: rect.right + 8,
        })
        setCopied(true)
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
        }
        timeoutRef.current = setTimeout(() => {
          setCopied(false)
          setPillPosition(null)
          timeoutRef.current = null
        }, 550)
      }

      handleCopy?.()
    },
    [handleCopy, showCopiedIndicator],
  )

  return (
    <DefaultKeyBox style={wrapperStyle}>
      {typeof content === 'string' ? (
        <Label>
          {truncate({ fullString: content, frontChars, backChars })}
        </Label>
      ) : (
        content
      )}

      <Flex style={{ position: 'relative' }} minWidth="18px" ref={buttonRef}>
        <StyledCopy
          as={Copy}
          color="light2"
          activeColor="green"
          focusColor="green"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onCopy(innerCopyContent)
          }}
          tabIndex={0}
          role="button"
          aria-label="Copy to clipboard"
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              e.stopPropagation()
              onCopy(innerCopyContent)
            }
          }}
        />
      </Flex>
      {showCopiedIndicator &&
        copied &&
        pillPosition &&
        createPortal(
          <InfoPill
            wrapperStyle={{
              position: 'fixed',
              top: `${pillPosition.top}px`,
              left: `${pillPosition.left}px`,
              zIndex: 99999,
            }}
          >
            <CheckCircle width="1.5rem" height="1.5rem" />
            <SmallLabel>Copied</SmallLabel>
          </InfoPill>,
          document.body,
        )}
    </DefaultKeyBox>
  )
}

export default function CopyBox({ wrapperStyle, ...props }: CopyProps) {
  return (
    <KeyBox style={wrapperStyle}>
      <CopyLabel {...props} />
    </KeyBox>
  )
}
